import { useEffect, useState } from "react";
import { StyleSheet, View, ActivityIndicator } from "react-native";

import { Chip, Typography, useTheme } from "@smartrent/ui";

import { useApi } from "@/lib/hooks";
import { formatDate } from "@/utils";

import { HubDebugTitleBar } from "./HubDebugTitleBar";

interface HubAlarm {
  id: string;
  alarm: string;
  alerted_at: string;
  cleared_at: string;
  unit_id: string;
  hub_serial: string;
}

interface AlarmDetails {
  name: string;
  details: string;
}

// Mapping of alarm types to their human-readable details
const ALARM_DETAILS: Record<string, AlarmDetails> = {
  "MacawSupport.RadarServer.RadarUnavailable": {
    name: "Radar Failure",
    details:
      "We detected a failure with the mmWave sensor, please create an RMA for this hub.",
  },
  "HVACSupport.SHT4X.UnusableReadings": {
    name: "Temperature Sensor Failure",
    details:
      "We detected a failure with the temperature sensor, please create an RMA for this hub.",
  },
  "HVACSupport.WallPlateEEPROMNotFound": {
    name: "Wall Plate Disconnected",
    details:
      "The hub has become disconnected from the wall plate, please work with onsite staff to address this issue or create an RMA.",
  },
  "MacawSupport.TouchscreenUnavailable": {
    name: "Touchscreen Failure",
    details:
      "We detected an issue with the hub's screen, please create an RMA for this hub.",
  },
};

interface HubAlarmsProps {
  serial: string;
  onFetch?: (data: any) => void;
}

export default function HubAlarms({ serial, onFetch }: HubAlarmsProps) {
  const [loading, setLoading] = useState(true);
  const [alarms, setAlarms] = useState<HubAlarm[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { colors } = useTheme();

  const { response, error: apiError } = useApi({
    url: `/hubs/${serial}/alarms`,
    options: {
      method: "GET",
    },
    trigger: [serial, refreshTrigger],
  });

  useEffect(() => {
    if (response && response.data) {
      setAlarms(response.data.alarms || []);
      setLoading(false);
      if (onFetch) {
        onFetch(response.data);
      }
    } else if (apiError) {
      setError("Error fetching hub alarms");
      setLoading(false);
    }
  }, [response, apiError, onFetch]);

  return (
    <View style={styles.container}>
      <HubDebugTitleBar
        title="Hub Alarms"
        isLoading={loading}
        onReloadClicked={() => {
          setLoading(true);
          setError(null);
          // Re-trigger the API call by incrementing the refresh trigger
          setRefreshTrigger((prev) => prev + 1);
        }}
      />

      {loading && (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" />
          <Typography style={styles.loadingText}>
            Loading hub alarms...
          </Typography>
        </View>
      )}

      {error && (
        <View
          style={[styles.errorContainer, { backgroundColor: colors.gray050 }]}
        >
          <Typography color="error">{error}</Typography>
        </View>
      )}

      {!loading &&
        !error &&
        (alarms.length === 0 ||
          !alarms.some((alarm) =>
            Object.keys(ALARM_DETAILS).includes(alarm.alarm)
          )) && (
          <View style={styles.emptyContainer}>
            <Typography color="gray600">No active alarms</Typography>
          </View>
        )}

      {!loading && !error && alarms.length > 0 && (
        <View style={styles.alarmsContainer}>
          {alarms
            // Only display alarms that are in our ALARM_DETAILS mapping
            .filter((alarm) => Object.keys(ALARM_DETAILS).includes(alarm.alarm))
            .map((alarm) => {
              // Get display details for this alarm type
              const details = ALARM_DETAILS[alarm.alarm];

              return (
                <View
                  key={alarm.id}
                  style={[
                    styles.alarmItem,
                    { borderBottomColor: colors.borderLow },
                  ]}
                >
                  <View style={styles.alarmHeader}>
                    <Typography
                      style={styles.alarmType}
                      variation="bold.title.five"
                    >
                      {details.name}
                    </Typography>
                    <Chip color="error">
                      {alarm.cleared_at ? "Cleared" : "Active"}
                    </Chip>
                  </View>

                  <View style={styles.alarmTimes}>
                    <Typography
                      style={styles.alarmTime}
                      variation="regular.caption.medium"
                      color="gray600"
                    >
                      Alerted: {formatDate({ date: alarm.alerted_at })}
                    </Typography>
                    {alarm.cleared_at && (
                      <Typography
                        style={styles.alarmTime}
                        variation="regular.caption.medium"
                        color="gray600"
                      >
                        Cleared: {formatDate({ date: alarm.cleared_at })}
                      </Typography>
                    )}
                  </View>

                  <View
                    style={[
                      styles.alarmDetailsContainer,
                      { backgroundColor: colors.backgroundDisabled },
                    ]}
                  >
                    <Typography
                      style={styles.alarmDetailsTitle}
                      variation="bold.title.five"
                    >
                      Details:
                    </Typography>
                    <Typography
                      style={styles.alarmDetailsText}
                      variation="regular.body.small"
                    >
                      {details.details}
                    </Typography>
                  </View>
                </View>
              );
            })}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
    borderRadius: 4,
    marginBottom: 16,
  },
  loadingContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  loadingText: {
    marginTop: 10,
  },
  errorContainer: {
    padding: 16,
    borderRadius: 4,
  },
  emptyContainer: {
    padding: 20,
    alignItems: "center",
  },
  alarmsContainer: {
    marginTop: 10,
  },
  alarmItem: {
    borderBottomWidth: 1,
    paddingVertical: 12,
  },
  alarmHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  alarmType: {
    flex: 1,
  },
  alarmTimes: {
    marginTop: 4,
  },
  alarmTime: {
    marginTop: 2,
  },
  alarmDetailsContainer: {
    marginTop: 10,
    padding: 8,
    borderRadius: 4,
  },
  alarmDetailsTitle: {
    marginBottom: 4,
  },
  alarmDetailsText: {
    lineHeight: 20,
  },
});
